import { Fragment, Ref, useMemo } from 'react';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { AccordionItem, AccordionItemRef } from '~/shared/components/Accordion';
import { Specification, SpecificationLine, Wrapper } from './styled';

const ProductSpecifications = ({
    product,
    ref,
}: {
    product?: ShopTypes.ProductQueryProduct;
    ref?: Ref<AccordionItemRef>;
}) => {
    const { translate } = useTranslations();

    const specifications = useMemo(() => {
        const set = new Set<string[]>();

        product?.item?.multiSpecifications?.forEach((item) => {
            item?.data?.forEach((value) => {
                set.add(value);
            });
        });
        product?.multiSpecifications?.forEach((item) =>
            item?.data?.forEach((value) => {
                set.add(value);
            }),
        );

        const title =
            product?.item?.multiSpecifications?.map((item) => item.title) ||
            product?.multiSpecifications?.map((item) => item.title);

        const data = Array.from(set);

        if (!data.length) {
            return undefined;
        }

        return { title, data };
    }, [product]);

    const itemNumbers = useMemo(() => {
        if (!product?.item?.ean && !product?.item?.supplierItemNumber) return null;

        return {
            ean: product?.item?.ean,
            supplierItemNumber: product?.item?.supplierItemNumber,
        };
    }, [product?.item?.ean, product?.item?.supplierItemNumber]);

    if (!specifications?.data?.length && !itemNumbers) {
        return null;
    }

    return (
        <AccordionItem ref={ref} id="specifications" header={translate('product.specifications')}>
            <Wrapper>
                <tbody>
                    <SpecificationLine>
                        <Specification>{translate('generic.ean')}</Specification>
                        <Specification>{itemNumbers?.ean}</Specification>
                    </SpecificationLine>

                    <SpecificationLine>
                        <Specification>{translate('generic.supplierItemNumber')}</Specification>
                        <Specification>{itemNumbers?.supplierItemNumber}</Specification>
                    </SpecificationLine>
                </tbody>
            </Wrapper>

            {(specifications?.data?.length || 0) > 0 ? (
                <Wrapper topMargin={!!specifications?.title}>
                    {specifications?.title ? (
                        <thead>
                            <tr>
                                <Specification isTitle colSpan={2}>
                                    {specifications.title.join('')}
                                </Specification>
                            </tr>
                        </thead>
                    ) : null}

                    {specifications?.data?.length ? (
                        <tbody>
                            {specifications.data?.map((spec, j) => (
                                <SpecificationLine key={j}>
                                    <Specification>{spec[0]}</Specification>

                                    <Specification>
                                        {spec
                                            .filter((_, index) => index > 0)
                                            .map((spec, index) => (
                                                <Fragment key={`${spec}${index}`}>{spec} </Fragment>
                                            ))}
                                    </Specification>
                                </SpecificationLine>
                            ))}
                        </tbody>
                    ) : null}
                </Wrapper>
            ) : null}
        </AccordionItem>
    );
};

export default ProductSpecifications;
